import { Text, Flex, Button, Card, Spinner, Select, TextField } from '@radix-ui/themes'
import { useMapService, useCurrentLocation } from '../hooks/useMapService.ts'
import { useState, useEffect, useRef } from 'react'
import type { Restaurant } from '@ablunch/map-service'
import { RecordForm } from '../components/RecordForm.tsx'
import { useAuthentication } from '../contexts/AuthenticationContext.tsx'

export const MapPage = () => {
  const { user } = useAuthentication()
  const [selectedMap, setSelectedMap] = useState<'kakao' | 'google' | 'naver' | 'apple'>('kakao')
  const mapService = useMapService(selectedMap)
  const { location, error: locationError, loading: locationLoading } = useCurrentLocation()
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMapMoved, setHasMapMoved] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null)
  const [recordFormOpen, setRecordFormOpen] = useState(false)
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [searchMode, setSearchMode] = useState<'nearby' | 'global' | null>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<any>(null)
  const mapCardRef = useRef<HTMLDivElement>(null)


  const searchNearbyRestaurants = async (searchLocation?: { latitude: number; longitude: number }) => {
    const targetLocation = searchLocation || location
    if (!mapService || !targetLocation) return

    setLoading(true)
    setError(null)

    try {
      const results = await mapService.searchNearby(targetLocation, 500) // 500m 반경
      setRestaurants(results)

      // 지도에 마커 업데이트
      if (mapInstanceRef.current && mapContainerRef.current) {
        mapInstanceRef.current = await mapService.renderMap(mapContainerRef.current, targetLocation, {
          zoom: 3,
          markers: results,
          onMapMove: () => {
            setHasMapMoved(true)
          }
        })
      }
    } catch (err) {
      setError('음식점 검색에 실패했습니다')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleKeywordSearch = async (mode: 'nearby' | 'global') => {
    if (!mapService || !searchKeyword.trim()) return

    setLoading(true)
    setError(null)
    setSearchMode(mode)

    try {
      const coordinates = mode === 'nearby' && location ? location : undefined
      const results = await mapService.searchByKeyword(searchKeyword.trim(), coordinates)
      setRestaurants(results)

      // 지도에 마커 업데이트
      if (mapInstanceRef.current) {
        mapService.updateMarkers(mapInstanceRef.current, results)
      }

      // 검색 결과가 있으면 첫 번째 결과로 지도 이동
      if (results.length > 0 && mapInstanceRef.current && mapContainerRef.current) {
        const firstResult = results[0]
        mapInstanceRef.current = await mapService.renderMap(mapContainerRef.current, firstResult.coordinates, {
          zoom: 3,
          markers: results,
          onMapMove: () => {
            setHasMapMoved(true)
          }
        })
      }
    } catch (err) {
      setError('키워드 검색에 실패했습니다')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleResetSearch = async () => {
    setSearchKeyword('')
    setSearchMode(null)
    if (location) {
      await searchNearbyRestaurants(location)
    }
  }

  const searchCurrentMapLocation = async () => {
    if (!mapService || !mapInstanceRef.current) return

    const currentCenter = (mapService as any).getMapCenter?.(mapInstanceRef.current)
    if (currentCenter) {
      setLoading(true)
      setError(null)

      try {
        const results = await mapService.searchNearby(currentCenter, 500)
        setRestaurants(results)

        // 지도를 새로 렌더링하지 않고 마커만 업데이트
        if (mapInstanceRef.current) {
          mapService.updateMarkers(mapInstanceRef.current, results)
        }

        setHasMapMoved(false)
      } catch (err) {
        setError('음식점 검색에 실패했습니다')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
  }

  const renderMap = async () => {
    if (!mapService || !location || !mapContainerRef.current) return
    
    try {
      mapInstanceRef.current = await mapService.renderMap(mapContainerRef.current, location, {
        zoom: 3,
        onMapMove: () => {
          setHasMapMoved(true)
        }
      })
    } catch (err) {
      console.error('Failed to render map:', err)
    }
  }

  const toggleFullscreen = async () => {
    if (!mapCardRef.current) return

    try {
      if (!document.fullscreenElement) {
        await mapCardRef.current.requestFullscreen()
        setIsFullscreen(true)
      } else {
        await document.exitFullscreen()
        setIsFullscreen(false)
      }
    } catch (error) {
      console.error('Fullscreen error:', error)
    }
  }

  const returnToCurrentLocation = async () => {
    if (!location || !mapService || !mapInstanceRef.current || !mapContainerRef.current) return

    try {
      // 지도를 현재 GPS 위치로 이동하고 재렌더링
      mapInstanceRef.current = await mapService.renderMap(mapContainerRef.current, location, {
        zoom: 3,
        markers: restaurants,
        onMapMove: () => {
          setHasMapMoved(true)
        }
      })

      // 현재 위치 기준으로 음식점 검색
      await searchNearbyRestaurants(location)
      setHasMapMoved(false)
    } catch (error) {
      console.error('Failed to return to current location:', error)
    }
  }

  const handleRestaurantClick = (restaurant: Restaurant) => {
    setSelectedRestaurantId(restaurant.id)

    if (mapService && mapInstanceRef.current) {
      // 마커 강조
      (mapService as any).highlightMarker?.(mapInstanceRef.current, restaurant.id)

      // 지도 중심을 해당 음식점으로 이동
      if (mapInstanceRef.current.setCenter) {
        const position = new window.kakao.maps.LatLng(
          restaurant.coordinates.latitude,
          restaurant.coordinates.longitude
        )
        mapInstanceRef.current.setCenter(position)
      }
    }
  }

  const handleRecordClick = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant)
    setRecordFormOpen(true)
  }

  const handleSubmitRecord = async (data: { restaurant: Restaurant; content: string; images: string[]; photoTakenAt?: string }) => {
    try {
      const response = await fetch('/api/records', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          restaurant: data.restaurant,
          content: data.content,
          images: data.images,
          photoTakenAt: data.photoTakenAt,
          userEmail: user?.email || 'unknown@ab180.co',
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to save record')
      }

      const result = await response.json()
      console.log('Record saved:', result)
      alert('기록이 저장되었습니다!')
    } catch (error) {
      console.error('Failed to save record:', error)
      throw error
    }
  }

  useEffect(() => {
    if (location && mapService) {
      renderMap()
      searchNearbyRestaurants()
    }
  }, [location, mapService, selectedMap])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])


  if (locationLoading) {
    return (
      <Flex direction="column" gap="4" align="center">
        <Spinner />
        <Text>현재 위치를 가져오는 중...</Text>
      </Flex>
    )
  }

  if (locationError) {
    return (
      <Flex direction="column" gap="4" align="center">
        <Text color="red">위치 권한이 필요합니다</Text>
        <Text size="2">{locationError}</Text>
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="4">
      {/* 키워드 검색 UI */}
      <Card>
        <Flex direction="column" gap="3">
          <Flex gap="2" align="end">
            <TextField.Root
              placeholder="음식점 이름 검색..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchKeyword.trim()) {
                  handleKeywordSearch('nearby')
                }
              }}
              style={{ flex: 1 }}
            />
          </Flex>
          <Flex gap="2" wrap="wrap">
            <Button
              onClick={() => handleKeywordSearch('nearby')}
              disabled={loading || !searchKeyword.trim()}
              variant={searchMode === 'nearby' ? 'solid' : 'soft'}
              size="2"
            >
              📍 주변에서
            </Button>
            <Button
              onClick={() => handleKeywordSearch('global')}
              disabled={loading || !searchKeyword.trim()}
              variant={searchMode === 'global' ? 'solid' : 'soft'}
              size="2"
            >
              🌍 전체
            </Button>
            <Button
              onClick={handleResetSearch}
              disabled={loading || searchMode === null}
              variant="outline"
              size="2"
            >
              ↻ 초기화
            </Button>
          </Flex>
        </Flex>
      </Card>

      <Flex justify="between" align="center">
        <Text size="5" weight="bold">
          {searchMode === 'nearby' ? `"${searchKeyword}" 주변 검색 결과` :
           searchMode === 'global' ? `"${searchKeyword}" 전체 검색 결과` :
           '주변 음식점'}
        </Text>
        <Flex gap="2" align="center">
          <Select.Root value={selectedMap} onValueChange={(value: any) => setSelectedMap(value)}>
            <Select.Trigger placeholder="지도 선택" />
            <Select.Content>
              <Select.Item value="kakao">카카오맵</Select.Item>
              <Select.Item value="google" disabled>구글맵 (준비중)</Select.Item>
              <Select.Item value="naver" disabled>네이버맵 (준비중)</Select.Item>
              <Select.Item value="apple" disabled>애플맵 (준비중)</Select.Item>
            </Select.Content>
          </Select.Root>
          <Button onClick={() => searchNearbyRestaurants()} disabled={loading}>
            새로고침
          </Button>
        </Flex>
      </Flex>

      {error && (
        <Card style={{ backgroundColor: '#fef2f2', padding: '12px' }}>
          <Flex direction="column" gap="2">
            <Text color="red" size="2" weight="bold">{error}</Text>
            <Text size="1" color="gray">
              카카오맵 API 키가 설정되어 있는지, 현재 도메인이 카카오 개발자 콘솔에 등록되어 있는지 확인해주세요.
            </Text>
          </Flex>
        </Card>
      )}

      <Card
        ref={mapCardRef}
        style={{
          height: isFullscreen ? '100vh' : '400px',
          padding: 0,
          backgroundColor: error ? '#f9fafb' : 'transparent',
          position: 'relative'
        }}
      >
        {error ? (
          <Flex align="center" justify="center" style={{ height: '100%' }}>
            <Text color="gray">지도를 표시할 수 없습니다</Text>
          </Flex>
        ) : (
          <>
            <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
            {hasMapMoved && (
              <Button
                onClick={searchCurrentMapLocation}
                disabled={loading}
                size="3"
                style={{
                  position: 'absolute',
                  top: '16px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  zIndex: 10
                }}
              >
                이 위치에서 검색
              </Button>
            )}
            <Flex
              direction="column"
              gap="2"
              style={{
                position: 'absolute',
                bottom: '16px',
                right: '16px',
                zIndex: 10
              }}
            >
              <Button
                onClick={returnToCurrentLocation}
                size="2"
                variant="solid"
                style={{
                  padding: 0,
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'white',
                  color: 'var(--gray-12)',
                  border: '1px solid var(--gray-6)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)'
                }}
              >
                ⊙
              </Button>
              <Button
                onClick={toggleFullscreen}
                size="2"
                variant="solid"
                style={{
                  padding: 0,
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'white',
                  color: 'var(--gray-12)',
                  border: '1px solid var(--gray-6)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)'
                }}
              >
                {isFullscreen ? '⊡' : '⛶'}
              </Button>
            </Flex>
          </>
        )}
      </Card>

      {loading ? (
        <Flex align="center" justify="center" style={{ minHeight: '200px' }}>
          <Spinner />
        </Flex>
      ) : (
        <Flex direction="column" gap="2">
          {restaurants.length === 0 ? (
            <Text color="gray">주변에 음식점이 없습니다</Text>
          ) : (
            restaurants.map((restaurant) => (
              <Card
                key={restaurant.id}
                onClick={() => handleRestaurantClick(restaurant)}
                style={{
                  cursor: 'pointer',
                  border: selectedRestaurantId === restaurant.id ? '2px solid var(--accent-9)' : undefined,
                  backgroundColor: selectedRestaurantId === restaurant.id ? 'var(--accent-2)' : undefined
                }}
              >
                <Flex direction="column" gap="2">
                  <Flex direction="column" gap="1">
                    <Text weight="bold">{restaurant.name}</Text>
                    <Text size="2" color="gray">{restaurant.address}</Text>
                    {restaurant.distance && (
                      <Text size="1" color="gray">{restaurant.distance}m</Text>
                    )}
                    {restaurant.category && (
                      <Text size="1">{restaurant.category}</Text>
                    )}
                  </Flex>
                  {selectedRestaurantId === restaurant.id && (
                    <Button
                      size="2"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRecordClick(restaurant)
                      }}
                    >
                      기록하기
                    </Button>
                  )}
                </Flex>
              </Card>
            ))
          )}
        </Flex>
      )}

      {selectedRestaurant && (
        <RecordForm
          restaurant={selectedRestaurant}
          open={recordFormOpen}
          onClose={() => {
            setRecordFormOpen(false)
            setSelectedRestaurant(null)
          }}
          onSubmit={handleSubmitRecord}
        />
      )}
    </Flex>
  )
}